import React, { useEffect, useState } from "react";
import Checkbox from "../../../../library/Checkbox";
import IconButton from "../../../../library/IconButton";
import PermissionsGate from "../../../../../utils/permissionGate";
import Hover from "../../../../common/Hover";
import Str from "../../../../common/Str";
import SkeletonTicketList from "../../../../../loaders/SkeletonTicketList";
import { tokens } from "../../../../../public/tokens";
import { twoDecimalRound } from "../../../../../utils/commonFunction";
import {
    StudentAvatar,
    StyledTable,
    TableBookResponsive,
    TableBookScroll,
    TableBookScrollbarTrack,
    TableBookScrollbarInner,
    StyledHeaderRow,
    StyledHeaderCell,
    SortableHeaderCell,
    HeaderLabel,
    SortIcon,
    CheckboxHeaderCell,
    StyledRow,
    StyledCell,
    CheckboxCell,
    CheckboxCellContent,
    StickyHeaderCell,
    StickySortableHeaderCell,
    StickyCheckboxHeaderCell,
    StickyCell,
    StickyCheckboxCell,
    StudentCellContent,
    StudentName,
    OriginLink,
    LatestIntakeText,
    ModuleHeaderTitle,
    ModuleHeaderLink,
    ModuleHeaderIcon,
    ModuleHeaderName,
    ModuleHeaderCell,
    ModuleCell,
    ModuleCellContent,
    ModuleCellCenter,
    ModuleIntakeLink,
    ModuleMarkRow,
    ModuleMarkText,
    ConditionIcon,
    ExemptToggleButton,
    ConditionTag,
    ConditionTagIcon,
    ExemptCellContent,
    ExemptLabel,
    ExemptLabelIcon,
    ResultBadge,
    ActionCellContent,
    ShadowedCalculateButton,
    NoRecordsMessage,
} from "./PMCGradeBook.styles";

const MODULE_CONDITION_VISUALS = {
    pass: { tone: "success", icon: "fas fa-check-circle", label: "Pass" },
    fail: { tone: "alert", icon: "fas fa-times-circle", label: "Fail" },
    condoned: { tone: "info", icon: "fas fa-check-circle", label: "Condoned" },
    "pending marking": { tone: "warning", icon: "fas fa-sync", label: "Pending" },
};

function getModuleConditionVisual(passStatus) {
    const key = passStatus ? passStatus.toString().replace("-", "").toLowerCase() : "";
    return MODULE_CONDITION_VISUALS[key] || { tone: "neutral", icon: "fal fa-info-circle", label: passStatus || "N/A" };
}

function getResultStatus(student) {
    if (student?.GraduationConditionMetDate != null) {
        return { key: "graduated", tone: "success", icon: "fas fa-graduation-cap", label: "Graduated" };
    }

    const hasPendingModule = (student?.modules || []).some((module) => {
        const details = module?.courseGradeDetails;
        if (module?.grant_exemption || !details || Object.keys(details).length === 0) {
            return false;
        }
        return !details.pass_status || details.pass_status === "N/A";
    });

    if (hasPendingModule) {
        return { key: "pending", tone: "warning", icon: "fas fa-sync", label: "Pending" };
    }

    return { key: "incomplete", tone: "alert", icon: "fas fa-times", label: "Incomplete" };
}

const STICKY_COL_WIDTHS = {
    checkbox: 48,
    student: 200,
    origin: 90,
    latest: 90,
    result: 120,
    action: 180,
};


/**
 * The PMC Gradebook's module-marks table: sortable Student/Origin/Latest columns, one
 * column per module (mark, condition, exemption toggle), the Result badge, and the
 * per-student Calculate/Download row actions. Handles its own loading skeleton and
 * empty state; selection/sort/exemption/calculate/download all delegate to the caller.
 */
function GradebookTable({
    isLoading = false,
    data,
    moduleHeader,
    paginationTotal = 0,
    selectedCount = 0,
    isRowSelected,
    isBusy = false,
    isSelecting = false,
    calculatingStudentId = null,
    sortConfig,
    onSort,
    onToggleSelectAll,
    onToggleSelectRow,
    onExemptionToggle,
    onSaveSingleGrade,
    onDownloadSOR,
    tableScrollRef,
    scrollbarProxyRef,
}) {
    const [tableOverflowWidth, setTableOverflowWidth] = useState(0);

    useEffect(() => {
        const scrollEl = tableScrollRef?.current;
        if (!scrollEl) {
            return;
        }

        const updateScrollWidth = () => setTableOverflowWidth(scrollEl.scrollWidth - scrollEl.clientWidth);
        updateScrollWidth();

        const resizeObserver = new ResizeObserver(updateScrollWidth);
        resizeObserver.observe(scrollEl);

        return () => resizeObserver.disconnect();
    }, [tableScrollRef, data, moduleHeader]);

    useEffect(() => {
        const scrollEl = tableScrollRef?.current;
        if (!scrollEl) {
            return;
        }

        let isDragging = false;
        let hasDragged = false;
        let startX = 0;
        let startScrollLeft = 0;

        const isInteractiveTarget = (target) =>
            Boolean(target.closest("button, a, input, select, textarea, label, [role='button']"));

        const startDragging = (event) => {
            if (event.button !== 0 || isInteractiveTarget(event.target)) {
                return;
            }
            isDragging = true;
            hasDragged = false;
            startX = event.pageX;
            startScrollLeft = scrollEl.scrollLeft;
            scrollEl.style.cursor = "grabbing";
        };

        const stopDragging = () => {
            isDragging = false;
            scrollEl.style.cursor = "grab";
        };

        const onMouseMove = (event) => {
            if (!isDragging) {
                return;
            }
            const delta = event.pageX - startX;
            if (Math.abs(delta) > 3) {
                hasDragged = true;
            }
            event.preventDefault();
            scrollEl.scrollLeft = startScrollLeft - delta;
        };

        const suppressClickAfterDrag = (event) => {
            if (hasDragged) {
                event.preventDefault();
                event.stopPropagation();
                hasDragged = false;
            }
        };

        scrollEl.style.cursor = "grab";
        scrollEl.addEventListener("mousedown", startDragging);
        scrollEl.addEventListener("mousemove", onMouseMove);
        scrollEl.addEventListener("mouseup", stopDragging);
        scrollEl.addEventListener("mouseleave", stopDragging);
        scrollEl.addEventListener("click", suppressClickAfterDrag, true);

        return () => {
            scrollEl.style.cursor = "";
            scrollEl.removeEventListener("mousedown", startDragging);
            scrollEl.removeEventListener("mousemove", onMouseMove);
            scrollEl.removeEventListener("mouseup", stopDragging);
            scrollEl.removeEventListener("mouseleave", stopDragging);
            scrollEl.removeEventListener("click", suppressClickAfterDrag, true);
        };
    }, [tableScrollRef, data, moduleHeader]);

    useEffect(() => {
        const scrollEl = tableScrollRef?.current;
        if (!scrollEl) {
            return;
        }

        const ARROW_SCROLL_AMOUNT = 60;

        const isEditableTarget = (target) =>
            Boolean(target.closest("input, textarea, select, [contenteditable='true']"));

        const onKeyDown = (event) => {
            if (isEditableTarget(event.target)) {
                return;
            }
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                scrollEl.scrollLeft -= ARROW_SCROLL_AMOUNT;
            } else if (event.key === "ArrowRight") {
                event.preventDefault();
                scrollEl.scrollLeft += ARROW_SCROLL_AMOUNT;
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [tableScrollRef, data, moduleHeader]);

    if (isLoading) {
        return <SkeletonTicketList />;
    }

    if (!data?.length) {
        return <NoRecordsMessage>{Str.noRecord}</NoRecordsMessage>;
    }

    return (
        <TableBookResponsive>
            <TableBookScroll className="table-book-scroll" ref={tableScrollRef}>
                <StyledTable as="table">
                    <thead>
                        <StyledHeaderRow as="tr">
                            <StickyCheckboxHeaderCell as="th" $left={0} $width={STICKY_COL_WIDTHS.checkbox}>
                                <CheckboxCellContent>
                                    <Checkbox
                                        size="sm"
                                        checked={!!paginationTotal && selectedCount === paginationTotal}
                                        indeterminate={selectedCount > 0 && selectedCount !== paginationTotal}
                                        disabled={isBusy}
                                        onChange={onToggleSelectAll}
                                    />
                                </CheckboxCellContent>
                            </StickyCheckboxHeaderCell>
                            <StickySortableHeaderCell
                                as="th"
                                onClick={() => onSort("student")}
                                $left={STICKY_COL_WIDTHS.checkbox}
                                $width={STICKY_COL_WIDTHS.student}
                            >
                                <HeaderLabel $active={sortConfig.key === "student"}>
                                    Student
                                    <SortIcon
                                        className={sortConfig.key !== "student" ? "fal fa-angles-up-down" : sortConfig.direction === "asc" ? "fal fa-angle-up" : "fal fa-angle-down"}
                                        $active={sortConfig.key === "student"}
                                    />
                                </HeaderLabel>
                            </StickySortableHeaderCell>
                            <StickySortableHeaderCell
                                as="th"
                                onClick={() => onSort("origin")}
                                $left={STICKY_COL_WIDTHS.checkbox + STICKY_COL_WIDTHS.student}
                                $width={STICKY_COL_WIDTHS.origin}
                            >
                                <HeaderLabel $active={sortConfig.key === "origin"}>
                                    Origin
                                    <SortIcon
                                        className={sortConfig.key !== "origin" ? "fal fa-angles-up-down" : sortConfig.direction === "asc" ? "fal fa-angle-up" : "fal fa-angle-down"}
                                        $active={sortConfig.key === "origin"}
                                    />
                                </HeaderLabel>
                            </StickySortableHeaderCell>
                            <StickySortableHeaderCell
                                as="th"
                                onClick={() => onSort("latest")}
                                $left={STICKY_COL_WIDTHS.checkbox + STICKY_COL_WIDTHS.student + STICKY_COL_WIDTHS.origin}
                                $width={STICKY_COL_WIDTHS.latest}
                                $edge="left"
                            >
                                <HeaderLabel $active={sortConfig.key === "latest"}>
                                    Latest
                                    <SortIcon
                                        className={sortConfig.key !== "latest" ? "fal fa-angles-up-down" : sortConfig.direction === "asc" ? "fal fa-angle-up" : "fal fa-angle-down"}
                                        $active={sortConfig.key === "latest"}
                                    />
                                </HeaderLabel>
                            </StickySortableHeaderCell>
                            {moduleHeader?.length
                                ? moduleHeader.map((moduleColumn, index) => (
                                    <ModuleHeaderCell as="th" key={index}>
                                        <ModuleHeaderTitle>
                                            <ModuleHeaderLink
                                                target="_blank"
                                                to={`/program-management-center/modules/module/open/${moduleColumn?.ModuleId}`}
                                                title={moduleColumn?.Code}
                                            >
                                                {moduleColumn?.Code}
                                                <ModuleHeaderIcon className="fas fa-external-link-alt" />
                                            </ModuleHeaderLink>
                                            <ModuleHeaderName title={moduleColumn?.Portal}>
                                                {moduleColumn?.Portal}
                                            </ModuleHeaderName>
                                        </ModuleHeaderTitle>
                                    </ModuleHeaderCell>
                                ))
                                : (<StyledHeaderCell as="th"></StyledHeaderCell>)}
                            <StickyHeaderCell
                                as="th"
                                $right={STICKY_COL_WIDTHS.action}
                                $width={STICKY_COL_WIDTHS.result}
                                $edge="right"
                            >
                                Result
                            </StickyHeaderCell>
                            <StickyHeaderCell
                                as="th"
                                style={{ textAlign: "right" }}
                                $right={0}
                                $width={STICKY_COL_WIDTHS.action}
                            >
                                Action
                            </StickyHeaderCell>
                        </StyledHeaderRow>
                    </thead>
                    <tbody>
                        {data.map((student, rowIndex) => {
                            const resultStatus = getResultStatus(student);
                            const isGraduated = resultStatus.key === "graduated";
                            return (
                                <StyledRow as="tr" key={rowIndex}>
                                    <StickyCheckboxCell as="td" $left={0} $width={STICKY_COL_WIDTHS.checkbox}>
                                        <CheckboxCellContent>
                                            <Checkbox
                                                size="sm"
                                                checked={isRowSelected(student.id)}
                                                disabled={isBusy}
                                                onChange={() => onToggleSelectRow(student)}
                                            />
                                        </CheckboxCellContent>
                                    </StickyCheckboxCell>
                                    <StickyCell as="td" $left={STICKY_COL_WIDTHS.checkbox} $width={STICKY_COL_WIDTHS.student}>
                                        <StudentCellContent>
                                            <StudentAvatar>
                                                <i className="fal fa-user"></i>
                                            </StudentAvatar>
                                            <Hover
                                                firstName={student.full_name.split(" ")[0]}
                                                lastName={
                                                    student.full_name.split(" ").length
                                                        ? student.full_name.split(" ")[1]
                                                        : ""
                                                }
                                                photo={student.picture_me}
                                                email={student.email}
                                                mobile={student.mobile}
                                                status={student.status}
                                                activity_status={student.activity_status}
                                                showNumber={true}
                                                number={student.student_crm_id}
                                                right={true}
                                            />
                                            <PermissionsGate
                                                scopes={["casvview"]}
                                                RenderError={() => (
                                                    <span title={student.full_name}>
                                                        {student.full_name}
                                                    </span>
                                                )}
                                            >
                                                <StudentName
                                                    to={`/studentAdministration/students/open/${student?.id}/gradebook`}
                                                    title={student.full_name}
                                                >
                                                    {student.full_name}
                                                </StudentName>
                                            </PermissionsGate>
                                        </StudentCellContent>
                                    </StickyCell>
                                    <StickyCell
                                        as="td"
                                        $left={STICKY_COL_WIDTHS.checkbox + STICKY_COL_WIDTHS.student}
                                        $width={STICKY_COL_WIDTHS.origin}
                                    >{student?.qualification_redirection_id && student?.getOriginIntake?.length ?
                                        <OriginLink
                                            title={student.getOriginIntake?.split("-").slice(0, 2).join("-")}
                                            to={`/courseAdministration/Programmes/programme/open/${student?.qualification_redirection_id}/details`}
                                        >
                                            {student.getOriginIntake?.split("-").slice(0, 2).join("-")}
                                        </OriginLink>
                                        : "-"}</StickyCell>
                                    <StickyCell
                                        as="td"
                                        $left={STICKY_COL_WIDTHS.checkbox + STICKY_COL_WIDTHS.student + STICKY_COL_WIDTHS.origin}
                                        $width={STICKY_COL_WIDTHS.latest}
                                        $edge="left"
                                    >
                                        {(() => {

                                            const enrol = student?.getLatestEnrolment;
                                            if (!enrol) {
                                                return "-";
                                            }

                                            const parts = enrol.split("-");
                                            if (parts.length < 3) {
                                                return "-";
                                            }

                                            const intakeLabel = parts.slice(0, 2).join("-");  // "2020-2"

                                            return (
                                                <LatestIntakeText title={intakeLabel}>
                                                    {intakeLabel}
                                                </LatestIntakeText>
                                            );
                                        })()}
                                    </StickyCell>
                                    {moduleHeader?.length > 0 ? moduleHeader.map((moduleColumn) => {
                                                const module = student?.modules?.find(
                                                    (row) => row.ModuleId === moduleColumn?.ModuleId
                                                );
                                                const grantExemption = module?.courseGradeDetails?.grant_exemption;
                                                const courseGradeDetails = module?.courseGradeDetails;
                                                const hasValidMark = courseGradeDetails?.pass_status !== "N/A" && courseGradeDetails?.scoreRecieved !== "N/A" && courseGradeDetails?.gradeText !== "N/A";
                                                const markVisual = getModuleConditionVisual(courseGradeDetails?.pass_status);
                                                const hasCourseGradeDetails = courseGradeDetails && Object.keys(courseGradeDetails).length > 0;
                                                const cellStatus = !hasCourseGradeDetails
                                                    ? "not-enrolled"
                                                    : grantExemption
                                                        ? "exempt"
                                                        : (hasValidMark && (markVisual.tone === "success" || markVisual.tone === "info"))
                                                            ? "pass"
                                                            : (hasValidMark && markVisual.tone === "alert")
                                                                ? "fail"
                                                                : null;
                                                return (
                                                    <ModuleCell as="td" key={moduleColumn?.ModuleId} $status={cellStatus}>
                                                        {hasCourseGradeDetails ? (
                                                            grantExemption ? (
                                                                <ExemptCellContent>
                                                                    <ExemptLabel>
                                                                        <ExemptLabelIcon
                                                                            className="fal fa-info-circle"
                                                                            title="The student is exempt from this module"
                                                                        ></ExemptLabelIcon>
                                                                        Exempt
                                                                    </ExemptLabel>
                                                                    <ExemptToggleButton
                                                                        $tone="success"
                                                                        title="Remove Exemption"
                                                                        onClick={() => {
                                                                            onExemptionToggle(module?.ModuleId, student?.id, courseGradeDetails?.grant_exemption == 0 ? 1 : 0, module);
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-user-plus"></i>
                                                                    </ExemptToggleButton>
                                                                </ExemptCellContent>
                                                            ) : hasValidMark ? (
                                                                <ModuleCellContent>
                                                                    {courseGradeDetails?.intakes_number_year && (
                                                                        <ModuleIntakeLink
                                                                            target="_blank"
                                                                            to={`/courseAdministration/coursesdetails/${courseGradeDetails?.intake_id}/gradeBooks/show`}
                                                                            title={courseGradeDetails?.intakes_number_year}
                                                                        >
                                                                            <i className="far fa-rectangle-history"></i>
                                                                            {courseGradeDetails?.intakes_number_year}
                                                                        </ModuleIntakeLink>
                                                                    )}
                                                                    <ModuleMarkRow>
                                                                        <ConditionIcon $tone={markVisual.tone} className={markVisual.icon} title={markVisual.label}></ConditionIcon>
                                                                        <ModuleMarkText>
                                                                            {(() => {
                                                                                const details = courseGradeDetails;
                                                                                if ((details?.scoreRecieved && details?.scoreRecieved !== "N/A") || details?.scoreRecieved === 0) {
                                                                                    return typeof details?.scoreRecieved === "string" && details?.scoreRecieved.includes("%")
                                                                                        ? Math.round(parseFloat(details?.scoreRecieved.replace("%", ""))) + "%"
                                                                                        : details?.grading_type !== 3
                                                                                            ? (typeof details?.gradeText === "string" && details?.gradeText.includes("%")
                                                                                                ? Math.round(parseFloat(details?.gradeText.replace("%", ""))) + "%"
                                                                                                : typeof details?.gradeText === "number"
                                                                                                    ? details?.gradeText + "%"
                                                                                                    : details?.gradeText) || "N/A"
                                                                                            : twoDecimalRound(details?.getAssessmentCalculate_sum_ass_mark) + " / " + details?.getAssessmentCalculate_sum_ass_total_mark;
                                                                                }
                                                                                return "N/A";
                                                                            })()}
                                                                        </ModuleMarkText>
                                                                        <ExemptToggleButton
                                                                            $tone="alert"
                                                                            title="Grant Exemption"
                                                                            onClick={() => {
                                                                                onExemptionToggle(module?.ModuleId, student?.id, module?.courseGradeDetails?.grant_exemption == 0 ? 1 : 0, module);
                                                                            }}
                                                                        >
                                                                            <i className="fas fa-user-times"></i>
                                                                        </ExemptToggleButton>
                                                                    </ModuleMarkRow>
                                                                </ModuleCellContent>
                                                            ) : courseGradeDetails?.pass_status === "N/A" ? (
                                                                <ModuleCellCenter>
                                                                    <ConditionTag $tone="warning" title="Pending">
                                                                        <ConditionTagIcon className="fas fa-sync"></ConditionTagIcon>
                                                                        Pending
                                                                        <ConditionTagIcon
                                                                            className="fal fa-info-circle"
                                                                            title="The results for this module have not been calculated yet"
                                                                        ></ConditionTagIcon>
                                                                    </ConditionTag>
                                                                </ModuleCellCenter>
                                                            ) : (
                                                                <ModuleCellCenter>
                                                                    <span>N/A</span>
                                                                </ModuleCellCenter>
                                                            )
                                                        ) : (
                                                            <ModuleCellCenter>
                                                                <ConditionTag $tone="plum" title="Not Enrolled">
                                                                    <ConditionTagIcon
                                                                        className="fas fa-info-circle"
                                                                        title="The student is not currently enrolled for this module"
                                                                    ></ConditionTagIcon>
                                                                    Not Enrolled
                                                                </ConditionTag>
                                                            </ModuleCellCenter>
                                                        )}
                                                    </ModuleCell>
                                                );
                                            }) : <StyledCell as="td" />}
                                    <StickyCell
                                        as="td"
                                        $right={STICKY_COL_WIDTHS.action}
                                        $width={STICKY_COL_WIDTHS.result}
                                        $edge="right"
                                    >
                                        <ResultBadge $tone={resultStatus.tone}>
                                            <i className={resultStatus.icon}></i>
                                            {resultStatus.label}
                                        </ResultBadge>
                                    </StickyCell>
                                    <StickyCell as="td" $right={0} $width={STICKY_COL_WIDTHS.action}>
                                        <ActionCellContent>
                                            <PermissionsGate
                                                scopes={["casvview"]}
                                                RenderError={() => (
                                                    <ShadowedCalculateButton
                                                        variant="accent"
                                                        size="lg"
                                                        disabled
                                                        title="Calculate"
                                                    >
                                                        <i className="fal fa-sync"></i> Calculate
                                                    </ShadowedCalculateButton>
                                                )}
                                            >
                                                <ShadowedCalculateButton
                                                    variant="accent"
                                                    size="lg"
                                                    title={
                                                        isBusy
                                                            ? "A calculation is already running"
                                                            : isSelecting
                                                                ? "Busy selecting students"
                                                                : "Calculate"
                                                    }
                                                    disabled={student?.isDis || isGraduated || isBusy || isSelecting}
                                                    onClick={
                                                        async () =>
                                                            await onSaveSingleGrade(student?.id)
                                                    }
                                                >
                                                    <i
                                                        className={calculatingStudentId === student?.id ? "fal fa-circle-notch fa-spin" : "fal fa-sync"}
                                                        style={calculatingStudentId === student?.id ? { color: tokens.colors.brand.blue["700"] } : undefined}
                                                    ></i>
                                                    {calculatingStudentId === student?.id ? "Calculating..." : "Calculate"}
                                                </ShadowedCalculateButton>
                                            </PermissionsGate>
                                            <PermissionsGate
                                                scopes={["casvview"]}
                                                RenderError={() => (
                                                    <IconButton icon="download" variant="solid" size={36} disabled ariaLabel="Download Statement of Results" />
                                                )}
                                            >
                                                <IconButton
                                                    icon="download"
                                                    variant="solid"
                                                    size={36}
                                                    ariaLabel="Download Statement of Results"
                                                    disabled={isBusy || isSelecting}
                                                    disabledReason={isBusy ? "A calculation is already running" : "Busy selecting students"}
                                                    title="Download Statement of Results"
                                                    onClick={() => onDownloadSOR(student)}
                                                />
                                            </PermissionsGate>
                                        </ActionCellContent>
                                    </StickyCell>
                                </StyledRow>
                            );
                        })}
                    </tbody>
                </StyledTable>
            </TableBookScroll>
            {moduleHeader?.length > 0 && (
                <TableBookScrollbarTrack
                    ref={scrollbarProxyRef}
                    $left={STICKY_COL_WIDTHS.checkbox + STICKY_COL_WIDTHS.student + STICKY_COL_WIDTHS.origin + STICKY_COL_WIDTHS.latest}
                    $right={STICKY_COL_WIDTHS.result + STICKY_COL_WIDTHS.action}
                >
                    <TableBookScrollbarInner style={{ width: `calc(100% + ${tableOverflowWidth}px)` }} />
                </TableBookScrollbarTrack>
            )}
        </TableBookResponsive>
    );
}

export default GradebookTable;
